import { Button } from '~/components/common/Button'
import { Typography } from '~/components/common/Typography'
import { useBillingContext } from '~/contexts/BillingContext'
import { useModalContext } from '~/contexts/ModalContext'

/**
 * Message shown when a task result cannot be displayed because
 * it was executed after exceeding the credit limit.
 *
 * Behavior varies by plan:
 * - Flex users (base tier): Prompted to purchase credits or upgrade
 * - Paid plan users: Prompted to purchase more credits or upgrade
 */
export function CreditLimitExceededMessage() {
  const { openPlan } = useModalContext()
  const { allLicenses } = useBillingContext()
  const isFlexLicense =
    allLicenses?.is_free_license ||
    allLicenses?.licenses?.some(
      (lic: { license_name?: string; instance_metadata?: Record<string, unknown> }) =>
        lic.license_name === 'flex' || lic.instance_metadata?.is_flex
    )

  const getMessage = () => {
    if (isFlexLicense) {
      return 'You have no credits available. Purchase credits to view your results, or upgrade to a subscription plan for monthly credits.'
    }
    return 'This task was executed after your credit limit was reached. To view the results, purchase additional credits or upgrade your plan.'
  }

  const getButtonLabel = () => {
    if (isFlexLicense) return 'Purchase Credits'
    return 'Purchase Credits'
  }

  return (
    <div className='flex flex-col items-center justify-center h-full w-full p-8 gap-6'>
      <div className='flex flex-col items-center gap-3 max-w-md text-center'>
        <div className='w-16 h-16 rounded-full bg-tokens-status-warning-3 flex items-center justify-center'>
          <svg
            width='32'
            height='32'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='text-tokens-status-warning-1'
          >
            <path
              d='M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>

        <Typography
          fontFamily='geist'
          fontSize='titleLg'
          className='text-tokens-neutral-text-primary'
        >
          {isFlexLicense ? 'Credits Required' : 'Credit Limit Exceeded'}
        </Typography>

        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          className='text-tokens-neutral-text-secondary'
        >
          {getMessage()}
        </Typography>
      </div>

      <div className='flex gap-3'>
        <Button variant='primary' onClick={() => openPlan()}>
          {getButtonLabel()}
        </Button>
      </div>
    </div>
  )
}
